// Shared OPERATE-surface primitives — the small token-only building blocks the Monitoring / Connectors /
// Capability-map surfaces reuse (a £-from-pence formatter, a cost-against-cap meter, a horizontal count-bar
// list for the refusal/failure graph, and the tone maps for a run/connector state). Token-derived utilities
// only (G-181); no raw hex/px. Kept here so each surface component stays small (file/function ceilings, G-085).
//
// NOTHING here fabricates or derives a durable STATE — the tone maps only pick a governed colour for a state
// the OWNING module already recorded (G-182); the cost/cap figures are rendered exactly as the projection
// carries them (the surface never re-computes a cap or a total — the caps are surfaced, not enforced here).

import type { ReactNode } from 'react';
import type { Tone } from '../primitives.tsx';
import { Dot } from '../primitives.tsx';
import type { AuditOutcome, ConnectorState, RunState } from '../operate-projections.ts';

/** Format integer pence as a £ figure for the mono/tabular data role (display only — no re-computation). */
export function formatPence(pence: number): string {
  return `£${(pence / 100).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Map a run state to a governed tone — completed is quiet-go, failed/interrupted attention, running neutral. */
export function runStateTone(state: RunState): Tone {
  if (state === 'completed') return 'go';
  if (state === 'failed' || state === 'interrupted') return 'attention';
  return 'neutral';
}

/** Map a Connector state to a governed tone — connected go, error attention, configured neutral. */
export function connectorStateTone(state: ConnectorState): Tone {
  if (state === 'connected') return 'go';
  if (state === 'error') return 'attention';
  return 'neutral';
}

/** Map an audit outcome to a governed tone — a committed/recorded is quiet; any refusal reads as attention. */
export function outcomeTone(outcome: AuditOutcome): Tone {
  return outcome === 'committed' || outcome === 'recorded' ? 'neutral' : 'attention';
}

/** A cost-against-cap meter (N-FDE-2 AC-2) — the spend figure shown WITH its cap and a proportional fill. */
export function CostAgainstCap({
  label,
  spentPence,
  capPence,
  note,
}: {
  readonly label: string;
  readonly spentPence: number;
  readonly capPence: number;
  readonly note?: string;
}): React.JSX.Element {
  // The fill is a display ratio only; the cap is ENFORCED by kernel/agent, never here (the surface shows it).
  const ratio = capPence > 0 ? Math.min(spentPence / capPence, 1) : 0;
  const atCap = capPence > 0 && spentPence >= capPence;
  const pct = `${String(Math.round(ratio * 100))}%`;
  return (
    <div className="rounded-md border border-border bg-surface px-5 py-4" data-cost-cap={label}>
      <div className="ritona-numeric text-xs uppercase tracking-eyebrow text-text-subtle">
        {label}
      </div>
      <div className="ritona-numeric mt-2 flex items-baseline gap-2">
        <span className="text-3xl text-text" data-cost-spent>
          {formatPence(spentPence)}
        </span>
        <span className="text-sm text-text-subtle">of {formatPence(capPence)} cap</span>
      </div>
      <div
        className="mt-3 h-2 overflow-hidden rounded-full bg-surface-sunken"
        role="meter"
        aria-valuenow={Math.round(ratio * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label}: spend against cap`}
      >
        <div
          className={`h-full ${atCap ? 'bg-bracken' : 'bg-forest'}`}
          style={{ width: pct }}
          data-cap-reached={atCap ? 'true' : undefined}
        />
      </div>
      {note !== undefined ? <div className="mt-2 text-sm text-text-muted">{note}</div> : null}
    </div>
  );
}

/** One row in the count-bar graph — a labelled tally with a proportional bar (refusal/failure counts). */
export interface CountBarRow {
  readonly key: string;
  readonly label: string;
  readonly count: number;
  readonly tone: Tone;
}

/**
 * A horizontal count-bar list (N-FDE-2 AC-4) — the gate-refusal / failure COUNTS graphed (not grepped). Each
 * bar's width is a display proportion of the largest count; the counts are rendered exactly as the projection
 * tallied them. Rendered as a definition list so the count is a queryable behavioural signal, not copy.
 */
export function CountBars({
  rows,
  label,
}: {
  readonly rows: readonly CountBarRow[];
  readonly label: string;
}): React.JSX.Element {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0);
  return (
    <dl className="space-y-3" aria-label={label} data-count-bars>
      {rows.map((row) => {
        const width = max > 0 ? `${String(Math.round((row.count / max) * 100))}%` : '0%';
        return (
          <div key={row.key} className="grid grid-cols-[10rem_1fr_3rem] items-center gap-3">
            <dt className="flex items-center gap-2 text-sm text-text-muted">
              <Dot tone={row.tone} />
              {row.label}
            </dt>
            <div className="h-2 overflow-hidden rounded-full bg-surface-sunken">
              <div
                className={row.tone === 'attention' ? 'h-full bg-bracken' : 'h-full bg-forest'}
                style={{ width }}
              />
            </div>
            <dd className="ritona-numeric text-right text-sm text-text" data-count-for={row.key}>
              {row.count}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

/** A quiet mono meta line for a trace/connector card — a labelled value in the tabular data role. */
export function MetaLine({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <span className="ritona-numeric uppercase tracking-eyebrow text-text-subtle">{label}</span>
      <span className="text-text-muted">{children}</span>
    </div>
  );
}
